import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { FC, useEffect, useMemo, useState } from 'react';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { filterEligibleCustomFields, useContactCustomFields } from '../../../../queries/Contact/useContactCustomFields';
import { useAdvancedFiltersContext } from '../../../components-v2/AdvancedFilters/AdvancedFiltersContext';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemIndicator,
	ComboboxList,
} from '../../../components-v2/Combobox';
import { baseStyleSheet } from '../../../styles/styles';
import { styleSheet } from './styles';

// Re-export so existing import sites keep working without churn.
export { useHasEligibleContactCustomFields } from '../../../../queries/Contact/useContactCustomFields';

interface CustomFieldFiltersPanelProps {
	className?: string;
	styles?: (StyleDeclarationValue | false)[];
	disabled?: boolean;
	onSearch?: () => void;
	impersonationContext?: Api.IImpersonationContext;
}

export const CustomFieldFiltersPanel: FC<CustomFieldFiltersPanelProps> = ({
	className,
	styles = [],
	disabled = false,
	onSearch,
	impersonationContext,
}) => {
	const userSession = useUserSession();
	const hasCustomContactFields = userSession?.account?.additionalInfo?.hasCustomContactFields;

	const customFieldsQuery = useContactCustomFields({
		enabled: !!hasCustomContactFields,
		impersonationContext,
		refetchOnWindowFocus: false,
	});

	const eligibleFields = useMemo(
		() => filterEligibleCustomFields(customFieldsQuery.data?.fields),
		[customFieldsQuery.data?.fields]
	);

	const advancedFilters = useAdvancedFiltersContext();
	const { customFieldFilters } = advancedFilters;
	const { fieldName, setFieldName, value, setValue } = customFieldFilters;

	const selectedField = useMemo(
		() => eligibleFields.find(f => f.name === fieldName) ?? null,
		[eligibleFields, fieldName]
	);

	const [fieldSearchValue, setFieldSearchValue] = useState<string>(selectedField?.label ?? '');

	// Keep the input text in sync when the selection changes (e.g., loading a saved search).
	useEffect(() => {
		setFieldSearchValue(selectedField?.label ?? '');
	}, [selectedField]);

	if (eligibleFields.length === 0) {
		return null;
	}

	const canSearch = !disabled && fieldName.trim() !== '' && value.trim() !== '';

	const handleSearch = () => {
		if (!canSearch) {
			return;
		}
		onSearch?.();
	};

	return (
		<div className={`${css(styleSheet.container, ...(styles?.filter(Boolean) || []))} ${className || ''}`}>
			<div className={css(styleSheet.fieldContainer)}>
				<label htmlFor='custom-field-filter-name' className={css(styleSheet.fieldLabel)}>
					Field
				</label>
				<Combobox
					items={eligibleFields}
					value={selectedField}
					onValueChange={item => {
						const f = item as Api.IFormField<string> | null;
						setFieldName(f?.name ?? '');
					}}
					inputValue={fieldSearchValue}
					onInputValueChange={setFieldSearchValue}
					itemToStringLabel={item => (item as Api.IFormField<string>).label ?? ''}
					isItemEqualToValue={(a, b) => (a as Api.IFormField<string>).name === (b as Api.IFormField<string>).name}
				>
					<ComboboxInput id='custom-field-filter-name' placeholder='Select a field...' disabled={disabled} showClear />
					<ComboboxContent>
						<ComboboxEmpty>No matching fields</ComboboxEmpty>
						<ComboboxList>
							{(item: Api.IFormField<string>) => (
								<ComboboxItem key={item.name} value={item}>
									<ComboboxItemIndicator />
									{item.label}
								</ComboboxItem>
							)}
						</ComboboxList>
					</ComboboxContent>
				</Combobox>
			</div>
			<div className={css(styleSheet.fieldContainer)}>
				<label htmlFor='custom-field-filter-value' className={css(styleSheet.fieldLabel)}>
					Value
				</label>
				<input
					id='custom-field-filter-value'
					type='text'
					className={css(styleSheet.input)}
					value={value}
					onChange={e => setValue(e.target.value)}
					onKeyDown={e => {
						if (e.key === 'Enter') {
							e.preventDefault();
							handleSearch();
						}
					}}
					placeholder='Exact value (case-insensitive)'
					autoComplete='off'
					disabled={disabled}
				/>
			</div>
			<button
				type='button'
				className={css(baseStyleSheet.ctaButton, styleSheet.searchButton)}
				onClick={handleSearch}
				disabled={!canSearch}
			>
				Search
			</button>
		</div>
	);
};
